/**
 * MUTAGIM.SHOP — Auth Context (Phase 9)
 * Gestion de l'authentification utilisateur
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mutagim_token'));
  const [loading, setLoading] = useState(true);

  // Vérifier le token au chargement
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('mutagim_token');
      if (savedToken) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${savedToken}`,
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(savedToken);
          } else {
            // Token invalide
            localStorage.removeItem('mutagim_token');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('mutagim_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('mutagim_token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        toast.success('Connexion réussie');
        return { success: true, user: data.user };
      } else {
        toast.error(data.detail || 'Erreur de connexion');
        return { success: false, error: data.detail };
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Erreur de connexion');
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('mutagim_token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        toast.success('Compte créé avec succès');
        return { success: true, user: data.user };
      } else {
        toast.error(data.detail || 'Erreur lors de l\'inscription');
        return { success: false, error: data.detail };
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error('Erreur lors de l\'inscription');
      return { success: false, error: 'Network error' };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('mutagim_token');
    setToken(null);
    setUser(null);
    toast.info('Déconnexion réussie');
  }, []);

  const isAdmin = user?.role === 'admin';
  const isAuthenticated = !!user && !!token;

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
